import { useState, type FormEvent } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { api, errorMessage } from '../lib/api';
import { useAuthStore } from '../lib/auth.store';
import { TriDot } from '../ui/TriDot';
import { Icon } from '../ui/Icons';

export const Login = () => {
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);
  const setSession = useAuthStore((s) => s.setSession);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (accessToken) return <Navigate to="/" replace />;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { data } = await api.post('/api/auth/login', { email, password });
      setSession(data.accessToken, data.refreshToken, data.user);
      try {
        const { data: me } = await api.get('/api/auth/me');
        useAuthStore.getState().setUser(me.user);
      } catch {
        /* non-fatal — proceed with what we have */
      }
      navigate('/', { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#FAFAFB] text-ink font-sans">
      {/* Ambient radial wash — top-right indigo, bottom-left gray */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(120% 80% at 80% 10%, rgba(99,102,241,.18) 0%, rgba(99,102,241,0) 55%), radial-gradient(80% 60% at 0% 100%, rgba(209,213,219,.5) 0%, rgba(209,213,219,0) 50%)',
        }}
      />
      {/* Giant tri-dot motif */}
      <svg
        viewBox="0 0 600 200"
        className="absolute pointer-events-none"
        style={{ right: -120, top: 90, width: 880, opacity: 0.14 }}
      >
        <circle cx="100" cy="100" r="100" fill="#9CA3AF" />
        <circle cx="340" cy="100" r="100" fill="#6366F1" />
        <circle cx="580" cy="100" r="100" fill="#6366F1" />
      </svg>
      {/* Faint grid */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.35] pointer-events-none">
        <defs>
          <pattern id="grid" width="56" height="56" patternUnits="userSpaceOnUse">
            <path d="M56 0H0V56" fill="none" stroke="#E5E5EA" strokeWidth="0.6" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />
      </svg>

      <div className="relative min-h-screen flex items-center justify-center p-10">
        <div className="grid w-full max-w-[1040px] gap-20 items-center grid-cols-1 lg:grid-cols-[1fr_420px]">
          {/* Left — editorial pitch */}
          <div>
            <img
              src="/replai-logo-black-animated.svg?v=1"
              alt="replai"
              className="h-20 mb-10 w-auto"
            />

            <div className="inline-flex items-center gap-2 py-1.5 pl-2 pr-2.5 rounded-full bg-white/70 border border-line text-[11.5px] text-ink-muted mb-5">
              <TriDot size={4} gap={3} tone="mixed" animate />
              <span>Reply, smarter.</span>
            </div>

            <h1 className="font-serif font-normal text-[56px] leading-[1.02] tracking-tightest text-ink m-0">
              The chat agent<br />
              that <em className="text-accent not-italic font-serif italic">actually</em> replies<br />
              for you.
            </h1>
            <p className="mt-4 text-[14.5px] leading-relaxed text-ink-muted max-w-[380px]">
              One panel to train your agent, watch every conversation, and turn quiet
              visitors into qualified leads — while you sleep.
            </p>

            <div className="mt-7 flex flex-wrap gap-x-[18px] gap-y-2 text-[11.5px] text-ink-subtle">
              <span>● 24 / 7 coverage</span>
              <span>● Verbatim transcripts</span>
              <span>● Lead capture built-in</span>
            </div>
          </div>

          {/* Right — login card */}
          <div className="bg-white rounded-2xl p-7 border border-line shadow-lifted">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-lg font-semibold text-ink tracking-tight">Sign in</div>
                <div className="text-[12.5px] text-ink-muted mt-0.5">Welcome back to your panel.</div>
              </div>
              <TriDot size={5} gap={3} tone="mixed" />
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
              <div>
                <div className="flex justify-between items-baseline mb-1.5">
                  <label htmlFor="email" className="text-[11.5px] font-medium text-ink-soft">Email</label>
                </div>
                <input
                  id="email"
                  type="email"
                  className="input"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                />
              </div>

              <div>
                <div className="flex justify-between items-baseline mb-1.5">
                  <label htmlFor="password" className="text-[11.5px] font-medium text-ink-soft">Password</label>
                  <span className="text-[11.5px] text-accent">Contact admin</span>
                </div>
                <div className="relative">
                  <input
                    id="password"
                    type={showPw ? 'text' : 'password'}
                    className="input pr-10"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    aria-label={showPw ? 'Hide password' : 'Show password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-subtle hover:text-ink-soft transition-colors"
                  >
                    <Icon.Eye size={15} />
                  </button>
                </div>
              </div>

              {error && (
                <div className="text-[12.5px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || !email || !password}
                className="mt-1.5 inline-flex items-center justify-center gap-2 px-3.5 py-2.5 rounded-[10px] bg-ink text-white text-sm font-medium hover:bg-ink-soft transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Signing in…' : (
                  <>Sign in <Icon.ArrowRight size={14} /></>
                )}
              </button>

              <div className="flex items-center gap-2.5 my-1.5 text-[11px] text-ink-subtle">
                <span className="flex-1 h-px bg-line" />
                <span>or</span>
                <span className="flex-1 h-px bg-line" />
              </div>

              <button
                type="button"
                disabled
                title="SSO coming soon"
                className="inline-flex items-center justify-center gap-2.5 px-3.5 py-2.5 rounded-[10px] border border-line-strong bg-white text-sm font-medium text-ink-soft disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span
                  className="inline-block w-3.5 h-3.5 rounded-full"
                  style={{ background: 'conic-gradient(from 0deg, #EA4335, #FBBC05, #34A853, #4285F4, #EA4335)' }}
                />
                Continue with Google
                <span className="text-[10px] text-ink-subtle ml-1">soon</span>
              </button>
            </form>

            <div className="mt-5 pt-4 border-t border-line text-[11.5px] text-ink-subtle text-center">
              By signing in you agree to our terms · v0.6.2
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
